// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.TableRow.jsx" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// ----------------------------------------------------------------------

import React, { Component, PropTypes } from 'react';
import { observer } from 'mobx-react';
import { propTypes as ObservablePropTypes } from 'mobx-react';
import classnames from 'classnames';
import { isNil, omit, getOr, invokeArgs, isEmpty } from 'lodash/fp';

import TableCell from './Izenda.TableCell';

@observer
class TableRow extends Component {
  static propTypes = {
    registerCell: PropTypes.func.isRequired,
    unregisterCell: PropTypes.func.isRequired,
    rowData: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    rowProps: PropTypes.func,
    columns: PropTypes.array,
    columnWidths: ObservablePropTypes.arrayOrObservableArray,
    index: PropTypes.number,
    isOver: PropTypes.bool,
    isHighLight: PropTypes.func,
    handleItemClick: PropTypes.func
  };

  renderCell = (column: any, index: number) => {
    const props = this.props;
    const { columnWidths, rowData } = props;
    const rest = omit(['newWidth'], column.props);
    let cellData = {};

    if (rest.fnGetCellData && rowData) {
      cellData = rest.fnGetCellData(rowData);
    }

    return (
      <TableCell
        key={index}
        rowData={rowData}
        type={"column-cell"}
        column={column}
        columnIndex={index}
        rowIndex={props.index}
        newWidth={columnWidths[index]}
        didUpdate={props.didUpdate}
        cellAfterRenderCallBack={props.cellAfterRenderCallBack}
        registerCell={props.registerCell}
        unregisterCell={props.unregisterCell}
        cellData={cellData}
        handleItemClick={props.handleItemClick}
        selectCell={props.selectCell}
        sourceForm={props.sourceForm}
        {...rest}
      />
    );
  };

  handleRowClick = (): void => {
    const { onClickRow, rowData } = this.props;

    onClickRow && onClickRow(rowData);
  };

  componentDidMount() {
    const { stickHorizontal, index, tableStore } = this.props;
    if (stickHorizontal && index > 0) {
      const firstColumns = tableStore.cellElements[0];
      const firstCell = firstColumns[0];
      const left = parseInt(firstCell.style.left);
      left > 0 && tableStore.setLeftPosition(firstColumns[index], left);
    }
  }

  render() {
    const props = this.props;
    const { connectDragSource, rowData, columns } = props;
    const draggable = !isNil(connectDragSource);

    if (rowData && (rowData.deleted || rowData.hidden)) {
      return <div />;
    }

    const rowProps = invokeArgs('rowProps', [rowData], props);
    const rowClasses = getOr('', 'className', rowProps);
    const item = props.item || {};

    const classes = classnames('table-row', rowClasses, {
      'can-drop': item.isDroppable && props.isOver,
      'cannot-drop': !item.isDroppable && props.isOver,
      'selectable': !!props.onClickRow,
      'highlight-row': props.isHighLight && props.isHighLight(rowData)
    });
    const headerClasses = classnames('table-cell', {
      'u-stickLeft': props.stickHorizontal
    });
    const styles = props.rowSpan ? { left: props.rowSpan.width } : {};

    const headerCells = [], normalCells = [];
    columns.forEach((column, index) => {
      column.props.isColumnHeader
        ? headerCells.push(this.renderCell(column, index))
        : normalCells.push(this.renderCell(column, index));
    });

    return draggable ? (
      props.connectDragPreview(
        <div {...rowProps} className={classes} onClick={this.handleRowClick}>
          {connectDragSource(<div className="icon-draggable table-cell" style={{paddingRight: '1px'}} />)}
          {columns.map(this.renderCell)}
        </div>
      )
    ) : props.stickHorizontal ? (
      <div {...rowProps} className={classes} onClick={this.handleRowClick}>
        {!props.isSupportSticky && <div style={styles} className="u-virtualStickLeft" />}
        {!isEmpty(headerCells) && <div className={headerClasses}>{headerCells}</div>}
        {normalCells}
      </div>
    ) : (
      <div {...rowProps} className={classes} onClick={this.handleRowClick}>
        {[...headerCells, ...normalCells]}
      </div>
    );
  }
}

export default TableRow;



// WEBPACK FOOTER //
// ./ui/share/components/controls/DataGrid/Izenda.TableRow.jsx